'use client';

import Image from 'next/image';
import { BRAND_LOGOS } from '@/constants/home';

interface RollingBrandLogosProps {
  className?: string;
  /** Show heading above the marquee. Default true (pricing page). */
  showHeading?: boolean;
  heading?: string;
  logos?: readonly { name: string; url: string }[];
}

/**
 * Infinite-scroll marquee of brand logos.
 * CSS is injected via a global <style> tag (no styled-jsx hash) to avoid FOUC
 * during Next.js hydration where styled-jsx styles arrive ~1-2s late.
 */
export const RollingBrandLogos = ({
  className = '',
  showHeading = true,
  heading = 'Trusted by hundreds of GTM teams',
  logos = BRAND_LOGOS,
}: RollingBrandLogosProps) => {
  const trackLogos = [...logos, ...logos];

  return (
    <>
      {/* Inline global style — no hash, applied immediately during SSR */}
      <style>{`
        .mb-marquee-mask {
          position: relative;
          overflow: hidden;
          width: 100%;
          -webkit-mask-image: linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%);
          mask-image: linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%);
        }
        .mb-marquee-track {
          display: flex;
          align-items: center;
          gap: 4.5rem;
          width: max-content;
          will-change: transform;
          animation: mb-scroll-logos 40s linear infinite;
        }
        .mb-marquee-item {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 140px;
          height: 55px;
        }
        .mb-marquee-mask:hover .mb-marquee-track {
          animation-play-state: paused;
        }
        @keyframes mb-scroll-logos {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (min-width: 768px) {
          .mb-marquee-track { gap: 5.625rem; }
          .mb-marquee-item  { min-width: 209px; }
        }
      `}</style>

      <div className={`rolling-brand-logos ${className}`}>
        {showHeading && (
          <p
            className='mb-6 text-center font-normal'
            style={{ fontSize: 'clamp(0.875rem, 1.1vw, 1.125rem)', color: '#64748B' }}
          >
            {heading}
          </p>
        )}

        <div className='mb-marquee-mask'>
          <div className='mb-marquee-track'>
            {trackLogos.map((logo, index) => (
              <div key={`${logo.name}-${index}`} className='mb-marquee-item'>
                <Image
                  src={logo.url}
                  alt={logo.name}
                  width={185}
                  height={49}
                  className='h-[33px] w-auto object-contain md:h-[49px]'
                  style={{ opacity: 0.6 }}
                  loading={index < logos.length ? 'eager' : 'lazy'}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default RollingBrandLogos;
